import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SETTINGS_PATH } from 'utils/settings';
import { getHashSectionId } from './settingsNavigation';
import useSettingsScrollSpy from './useSettingsScrollSpy';
import useSettingsSectionScroll from './useSettingsSectionScroll';

const useSettingsNavigation = (sectionIds) => {
    const history = useHistory();
    const location = useLocation();
    const [activeId, setActiveId] = useState(sectionIds[0]);
    const processedHashRef = useRef(null);
    const ids = useMemo(() => new Set(sectionIds), [sectionIds]);
    const { isScrollingRef, scrollToSection } = useSettingsSectionScroll();

    const handleActiveChange = useCallback((id) => {
        setActiveId((currentId) => (currentId === id ? currentId : id));
    }, []);

    useSettingsScrollSpy({
        onActiveChange: handleActiveChange,
        pausedRef: isScrollingRef,
        sectionIds,
    });

    const scrollTo = useCallback(
        (id, { behavior = 'smooth', push = true } = {}) => {
            if (!ids.has(id)) return;

            const didScroll = scrollToSection(id, { behavior });
            if (!didScroll) return;

            setActiveId(id);
            processedHashRef.current = `#${id}`;
            if (push && location.hash !== `#${id}`) {
                history.push({ pathname: SETTINGS_PATH, search: location.search, hash: `#${id}` });
            }
        },
        [history, ids, location.hash, location.search, scrollToSection]
    );

    useEffect(() => {
        if (processedHashRef.current === location.hash) return undefined;

        const hashId = getHashSectionId(location.hash);
        if (!hashId || !ids.has(hashId)) return undefined;

        processedHashRef.current = location.hash;
        const timer = window.setTimeout(() => {
            scrollTo(hashId, { behavior: 'auto', push: false });
        }, 0);

        return () => window.clearTimeout(timer);
    }, [ids, location.hash, scrollTo]);

    return { activeId, scrollTo };
};

export default useSettingsNavigation;
